import { ref, reactive, computed, watch, type Ref, inject } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { UploadFile as ElementPlusUploadFile } from 'element-plus';
import { useFileUploaderStore, type FileUploaderUploadedFiles } from '@/pinia/stores/file-upload/fileUploaderStore';
import { useFileUploadApi } from '@/composables/file-upload/use-file-upload-api';
import CONST from '@/globals/constants';
import type { FileUploadItem } from '@base-shared/types/file-uploader';
import type Utils from '@/globals/utils.js';
import type { ElUpload } from 'element-plus';

type ElUploadInstance = InstanceType<typeof ElUpload>;

export const TARGET_COMPONENTS = {
  SCHEDULE_PLAYER_SELECTED: 'schedulePagePlayerSelected',
  FILES_PAGE: 'filesPage',
  ALERT_PAGE: 'alertPage',
  LED_PAGE: 'ledPage',
  VIDEOWALL_PAGE: 'videowallPage',
  TEMPLATES_PAGE: 'projectTemplatesPage',
  EDIT_TEMPLATE_PAGE: 'editProjectTemplatePage',
  AUDIO_PAGE: 'createProjectAudioPage',
  SCHEDULE_LISTING: 'schedules'
} as const;

export const DROPZONE_IDS = {
  REGULAR_MEDIA: 'fileUploaderRegularMedia',
  SMART_ADDS: 'fileUploaderSmartAdds'
} as const;

const MEDIA_ROUTES = new Set([
  CONST.ROUTE_NAME_MEDIA,
  CONST.ROUTE_NAME_LED,
  CONST.ROUTE_NAME_VIDEOWALL,
  CONST.ROUTE_NAME_ALERT
]);

// jQuery is a global loaded via script tag
declare const $: any;

// ─── Public types ─────────────────────────────────────────────────────────────

export interface DropzoneState {
  id: string;
  extraClassnames: string;
  showUploadIcon: boolean;
  showUploadButtonAndText: boolean;
  multipleUpload: boolean;
  disabled: boolean;
}

export interface UploaderConfig {
  component: string;
  dropzoneId: string;
  playerId: string | null;
  setDropzoneInMediaListModal: boolean;
  mediaListUploadIconClicked: boolean;
  acceptedFileTypes: string[];
}

/**
 * Manages the file-uploader instance and dropzone configuration reactively.
 *
 * Replaces `setFileUploaderConfig`, the 7 boolean flags in `data()`, the
 * `dropzones` array, `fileUploaderConfig`, and `currentRouteName`.
 *
 * Two reactive inputs drive the config:
 * 1. **Route** – the target component and dropzone enable/disable state are
 *    derived automatically whenever the Vue Router route changes.
 * 2. **`fileUploaderStore.uploaderConfig`** – external components (e.g. `media.js`,
 *    `schedule.js`) push config overrides into the Pinia store; this composable
 *    applies them and handles the `triggerClickDropzone` signal.
 */
export function useFileUploader () {
  const { t } = useI18n();
  const route = useRoute();
  const fileUploaderStore = useFileUploaderStore();
  const api = useFileUploadApi();
  const $utils = inject('$utils') as typeof Utils;

  // Reactive states
  const dropzones = ref<DropzoneState[]>([
    {
      id: DROPZONE_IDS.REGULAR_MEDIA,
      extraClassnames: '',
      showUploadIcon: false,
      showUploadButtonAndText: false,
      multipleUpload: true,
      disabled: false
    },
    {
      id: DROPZONE_IDS.SMART_ADDS,
      extraClassnames: '',
      showUploadIcon: false,
      showUploadButtonAndText: false,
      multipleUpload: true,
      disabled: false
    }
  ]);

  const config = reactive<UploaderConfig>({
    component: '',
    dropzoneId: DROPZONE_IDS.REGULAR_MEDIA,
    playerId: null,
    setDropzoneInMediaListModal: false,
    mediaListUploadIconClicked: false,
    acceptedFileTypes: ['all']
  });

  const currentRouteName = ref<string | null>(null);

  /**
   * Holds references to the dropzone DOM elements, keyed
   * by dropzone ID. Used to access the DOM elements
   * directly when needed.
   */
  const dropzoneElementRefs = ref<Record<string, ElUploadInstance>>({});

  /**
   * Set by the external-config watcher when `triggerClickDropzone` is true.
   * The component watches this and calls `$refs[id][0]` to open the OS file
   * picker, then resets the ref to null.
   */
  const pendingDropzoneClick = ref<string | null>(null);

  // ── Derived boolean flags (replace the 7 `data()` properties) ──────────────

  const usedForFilesPage = computed(() => config.component === TARGET_COMPONENTS.FILES_PAGE);
  const usedForSchedulePagePlayerSelected = computed(() => config.component === TARGET_COMPONENTS.SCHEDULE_PLAYER_SELECTED);
  const usedForAlertPage = computed(() => config.component === TARGET_COMPONENTS.ALERT_PAGE);
  const usedForLEDPage = computed(() => config.component === TARGET_COMPONENTS.LED_PAGE);
  const usedForVideowallPage = computed(() => config.component === TARGET_COMPONENTS.VIDEOWALL_PAGE);
  const usedForProjectTemplatesPage = computed(() => config.component === TARGET_COMPONENTS.TEMPLATES_PAGE);
  const usedForEditProjectTemplatePage = computed(() => config.component === TARGET_COMPONENTS.EDIT_TEMPLATE_PAGE);
  const usedForProjectAudioPage = computed(() => config.component === TARGET_COMPONENTS.AUDIO_PAGE);

  /** True when the current route is any of the media-management pages. */
  const isMediaPage = computed(() =>
    MEDIA_ROUTES.has((currentRouteName.value ?? '') as any)
  );

  // ── Helpers ─────────────────────────────────────────────────────────────────

  function enableDropzone (id: string): void {
    const dz = dropzones.value.find(d => d.id === id);
    if (dz) dz.disabled = false;
  }

  function disableDropzone (id: string): void {
    const dz = dropzones.value.find(d => d.id === id);
    if (dz) dz.disabled = true;
  }

  function resetConfig (): void {
    config.component = '';
    config.dropzoneId = DROPZONE_IDS.REGULAR_MEDIA;
    config.playerId = null;
    config.setDropzoneInMediaListModal = false;
    config.mediaListUploadIconClicked = false;
    config.acceptedFileTypes = ['all'];
    dropzones.value.forEach(d => { d.disabled = false; });
  }

  /**
   * Applies a target-component string: resets the config to defaults and then
   * enables/disables dropzones according to the page type.
   */
  function applyTargetComponent (component: string): void {
    resetConfig();
    config.component = component;

    switch (component) {
      case TARGET_COMPONENTS.SCHEDULE_PLAYER_SELECTED:
        // Both dropzones remain enabled (already done by resetConfig)
        break;
      case TARGET_COMPONENTS.FILES_PAGE:
      case TARGET_COMPONENTS.ALERT_PAGE:
      case TARGET_COMPONENTS.LED_PAGE:
      case TARGET_COMPONENTS.VIDEOWALL_PAGE:
      case TARGET_COMPONENTS.TEMPLATES_PAGE:
      case TARGET_COMPONENTS.EDIT_TEMPLATE_PAGE:
      case TARGET_COMPONENTS.AUDIO_PAGE:
        disableDropzone(DROPZONE_IDS.SMART_ADDS);
        break;
    }
  }

  // ── Route watcher ───────────────────────────────────────────────────────────

  function applyRoute (routeObj: typeof route): void {
    const name = routeObj.name as string | null;

    // The schedule page with a selected player is identified by route name
    // 'schedules' + a scheduleId query param.
    if (name === TARGET_COMPONENTS.SCHEDULE_LISTING && routeObj.query.scheduleId) {
      currentRouteName.value = TARGET_COMPONENTS.SCHEDULE_PLAYER_SELECTED;
      applyTargetComponent(TARGET_COMPONENTS.SCHEDULE_PLAYER_SELECTED);
      return;
    }

    currentRouteName.value = name;

    switch (name) {
      case TARGET_COMPONENTS.SCHEDULE_PLAYER_SELECTED:
        applyTargetComponent(TARGET_COMPONENTS.SCHEDULE_PLAYER_SELECTED);
        break;

      case 'create-template-set':
        // Determine sub-page from fullPath / params
        if ((routeObj.fullPath as string).includes('/create/audio') || routeObj.params.pageContent === 'audio') {
          applyTargetComponent(TARGET_COMPONENTS.AUDIO_PAGE);
        } else if (
          (routeObj.fullPath as string).includes('/create/new') ||
          (routeObj.params.setId && routeObj.params.templateId)
        ) {
          applyTargetComponent(TARGET_COMPONENTS.EDIT_TEMPLATE_PAGE);
        } else {
          applyTargetComponent(TARGET_COMPONENTS.TEMPLATES_PAGE);
        }
        break;

      case 'create-project-template':
      case 'edit-project-template':
        applyTargetComponent(TARGET_COMPONENTS.EDIT_TEMPLATE_PAGE);
        break;

      case CONST.ROUTE_NAME_MEDIA:
        applyTargetComponent(TARGET_COMPONENTS.FILES_PAGE);
        break;

      case CONST.ROUTE_NAME_ALERT:
        applyTargetComponent(TARGET_COMPONENTS.ALERT_PAGE);
        break;

      case CONST.ROUTE_NAME_LED:
        applyTargetComponent(TARGET_COMPONENTS.LED_PAGE);
        break;

      case CONST.ROUTE_NAME_VIDEOWALL:
        applyTargetComponent(TARGET_COMPONENTS.VIDEOWALL_PAGE);
        break;

      default:
        applyTargetComponent(TARGET_COMPONENTS.SCHEDULE_LISTING);
    }
  }

  // Fires immediately on setup so the config is correct before the first render.
  watch(route, applyRoute, { immediate: true, deep: true });

  // ── External config watcher (Pinia store) ──────────────────────────────────

  watch(
    () => fileUploaderStore.uploaderConfig,
    (newCfg) => {
      if (!newCfg || Object.keys(newCfg).length === 0) return;

      if (newCfg.disabledMediaUpload) {
        disableDropzone(DROPZONE_IDS.REGULAR_MEDIA);
        disableDropzone(DROPZONE_IDS.SMART_ADDS);
      }

      if (newCfg.targetComponent) {
        applyTargetComponent(newCfg.targetComponent as string);
      }

      if (newCfg.targetDropzoneId) {
        const id = newCfg.targetDropzoneId as string;
        (id === DROPZONE_IDS.SMART_ADDS)
          ? enableDropzone(DROPZONE_IDS.SMART_ADDS)
          : enableDropzone(DROPZONE_IDS.REGULAR_MEDIA);
        config.dropzoneId = id;
      }

      if (newCfg.targetPlayerId !== undefined) {
        config.playerId = newCfg.targetPlayerId as string | null;
      }

      if (newCfg.setDropzoneInMediaListModal) {
        config.setDropzoneInMediaListModal = true;
      }

      if (newCfg.unsetDropzoneInMediaListModal) {
        config.setDropzoneInMediaListModal = false;
        enableDropzone(DROPZONE_IDS.REGULAR_MEDIA);
        enableDropzone(DROPZONE_IDS.SMART_ADDS);
      }

      config.acceptedFileTypes = newCfg.acceptedFileTypes
        ? (newCfg.acceptedFileTypes as string[])
        : ['all'];

      // Signal the component to trigger a file-picker click (DOM interaction
      // stays in the component since it needs access to $refs).
      if (newCfg.triggerClickDropzone) {
        pendingDropzoneClick.value = (newCfg.targetDropzoneId as string | undefined) ??
        DROPZONE_IDS.REGULAR_MEDIA;
      }
    },
    { deep: true }
  );

  /**
   * When an external component requests a dropzone click via the Pinia store,
   * useFileUploader sets pendingDropzoneClick. We perform the DOM click here
   * because it requires access to the el-upload component refs.
   */
  watch(pendingDropzoneClick, (dropzoneId) => {
    if (!dropzoneId) return;

    const id = dropzoneId === DROPZONE_IDS.SMART_ADDS
      ? `#${DROPZONE_IDS.SMART_ADDS}`
      : `#${DROPZONE_IDS.REGULAR_MEDIA}`;

    $(id).find('.el-upload-dragger').trigger('click');
    pendingDropzoneClick.value = null;
  });

  // ── DOM-adjacent helpers ────────────────────────────────────────────────────

  /**
   * Called when a file is dropped onto the schedule-page dropzone.
   * Reads the active player ID from the DOM to keep `config.dropzoneId`
   * and `config.playerId` in sync with the hovered schedule item.
   * No-op outside the schedule-page context.
   */
  function syncDropzoneFromDom (): void {
    if (config.component !== TARGET_COMPONENTS.SCHEDULE_PLAYER_SELECTED) return;

    $('#fileUploader').find('.file-uploader-item').each((_indx: any, fileUploaderItem: any) => {
      const $item = $(fileUploaderItem);
      const classAttr = $item.attr('class') ?? '';
      const hasPlayerIdClass = classAttr.search(/(\bplayerId-\S+\b)/ig) > -1;

      if ($item.find('.el-upload').length && $item.find('.el-upload-dragger').length && hasPlayerIdClass) {
        config.dropzoneId = $item.attr('id') ?? config.dropzoneId;
        config.playerId = classAttr.match(/(\bplayerId-\S+\b)/ig)?.[0].substring(9) ?? null;
      }
    });
  }

  /** Clears all Element Plus upload input values to allow re-selecting the same file. */
  function clearInput (): void {
    document.querySelectorAll<HTMLInputElement>('.el-upload__input').forEach(input => {
      if (input) input.value = '';
    });
  }

  /**
   * Clear all files in the dropzone by calling the
   * dropzone component's clearFiles method.
   */
  function clearFilesInDropzone () {
    const dropzoneId = config.dropzoneId;
    if (typeof dropzoneElementRefs.value[dropzoneId] !== 'undefined') {
      dropzoneElementRefs.value[dropzoneId].clearFiles();
    }
  }

  /**
   * Triggers hook function in :http-request prop in
   * {@link ElUploadInstance} component to submit all
   * files in the dropzone and initiate upload to S3.
   */
  function submitFilesInDropzone () {
    const dropzoneId = config.dropzoneId;
    if (typeof dropzoneElementRefs.value[dropzoneId] !== 'undefined') {
      dropzoneElementRefs.value[dropzoneId].submit();
    }
  }

  // ── File Uploader helpers ───────────────────────────────────────────────────

  /**
   * Store the last event to the Pinia store to update the uploaded files state.
   * @param prevAction - 'setDefaultData' | 'deletedFile' | 'processNewFilesWithInstance'.
   * @param data - An object containing the relevant data to be emitted with the event. This can include the current list of files, the deleted file, or any other necessary information.
   */
  function storeUploadEvent (
    prevAction: 'setDefaultData' | 'deletedFile' | 'processNewFilesWithInstance' | 'finishedFile',
    data: FileUploaderUploadedFiles
  ) {
    fileUploaderStore.setUploadedFiles({ ...data, prevAction });
  }

  // ── File Uploader utilities ───────────────────────────────────────────────────

  /**
   * Validates a file against the current config's accepted
   * file types and the target component.
   * @param fileType - The type of the file (e.g., 'image', 'video', 'audio', etc.).
   */
  function isFileTypeValid (fileType: string): boolean {
    if (
      config.acceptedFileTypes.indexOf('all') === -1 &&
      config.acceptedFileTypes.indexOf(fileType) === -1
    ) {
      return false;
    } else if (usedForAlertPage.value && fileType === 'audio') {
      return false;
    } else if (usedForProjectAudioPage.value && fileType !== 'audio') {
      return false;
    } else if (
      fileType === 'audio' &&
      usedForSchedulePagePlayerSelected.value &&
      config.dropzoneId === DROPZONE_IDS.SMART_ADDS
    ) {
      return false;
    }

    return true;
  }

  /**
   * Determines the file type based on the Element Plus upload file object.
   * @param file - The Element Plus upload file object to check.
   * @returns The type of the file as a string ('video', 'img', 'audio', '7z') or null if unknown.
   */
  function getTypeFromElementPlus (file: ElementPlusUploadFile): string | null {
    if (file.raw && file.raw.type.includes('video')) {
      return 'video';
    } else if (file.raw && file.raw.type.includes('image')) {
      return 'img';
    } else if (file.raw && file.raw.type.includes('audio')) {
      return 'audio';
    } else if (file.raw && ((!file.raw.type && file.raw.name.split('.').pop() === '7z') || file.raw.type === '7z')) {
      return '7z';
    }

    return null;
  }

  /**
   * Returns the default `_edit` object for a new file upload entry.
   * @returns The default `_edit` object for a new file upload entry.
   */
  function getUploadFileDefaultEditData () {
    return {
      getMediaFullDetails: { requestStatus: null, media: null },
      addToSchedule: {
        media: null,
        selectedPlayerScheduleDetails: null,
        addToScheduleRequestStarted: null,
        addToScheduleRequestEnd: null,
        addToScheduleRequestStatus: 'pending',
        optimisticUpdate: []
      },
      updateMediaDataOnceConversionFinished: {
        updateMediaDataRequestStatus: null,
        updateMediaDataRequestStarted: null,
        updateMediaDataRequestEnd: null,
        updateMediaRequestData: null
      },
      resourceId: null,
      uploadRequests: {
        createMediaInstanceStatus: 'requesting',
        createMediaInstanceStart: DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss'), // moment().format('YYYY-MM-DD HH:mm:ss'),
        createMediaInstanceEnd: '',
        createMediaInstanceResponseData: {},
        createMediaInstanceStatusErrorType: '',
        submitToS3Status: 'notYetStarted',
        submitToS3Start: '',
        submitToS3End: '',
        submitToS3Message: 'Not yet Started',
        conversionFinished: false
      },
      existingFile: false,
      post: { url: null, fields: null },
      generatedFilename: '',
      targetComponent: config.component,
      targetDropzoneId: config.dropzoneId,
      targetPlayerId: config.playerId,
      uploadEventUsingMediaListModalOnClick: config.mediaListUploadIconClicked,
      isFromMediaListModal: config.setDropzoneInMediaListModal
    }
  }

  /**
   * Translates normal error descriptions for file uploads
   * based on the status code and error name.
   * @returns An array of file upload items with translated error descriptions.
   */
  function translateNormalErrorDescription (files: (FileUploadItem & { _edit: NonNullable<FileUploadItem['_edit']> })[]) {
    return files.map(file => {
      const statusCode = file?._edit?.uploadRequests?.createMediaInstanceResponseData?.statusCode;
      const errorName = file?._edit?.uploadRequests?.createMediaInstanceResponseData?.name;
      if (statusCode === 403 && errorName === 'AccessDeniedException') {
        file._edit.uploadRequests.createMediaInstanceResponseData.description = t('fileUpload.notAllowedToUploadMedia');
      }

      return file;
    });
  }

  // ── Duplicate file resolution ─────────────────────────

  /**
   * Perform the resolution of duplicate files by sending
   * requests to the backend with either 'copy' or 'replace'.
   * @param duplicateFiles - The list of files that have duplicate name errors and need resolution.
   * @param mode - The mode of resolution, either 'copy' or 'replace'.
   * @param alert - Optional boolean to indicate if an alert should be shown for the resolution.
   */
  async function resolveDuplicateFiles (
    duplicateFiles: FileUploadItem & { _edit: NonNullable<FileUploadItem['_edit']> }[],
    mode: 'copy' | 'replace',
    alert?: boolean
  ) {
    const results = [];

    for (const file of duplicateFiles) {
      const requestData = $utils.clone(file._edit.uploadRequests.createMediaInstanceOrigRequestData)
      if (requestData._edit) delete requestData._edit

      requestData.alert = !!alert
      const result = await api.resolveMediaInstance(requestData, mode)
      results.push(result)
    }

    return results;
  }

  return {
    // State
    dropzones,
    config,
    currentRouteName: currentRouteName as Readonly<Ref<string | null>>,
    dropzoneElementRefs,
    pendingDropzoneClick,
    // Derived flags
    isMediaPage,
    usedForFilesPage,
    usedForSchedulePagePlayerSelected,
    usedForAlertPage,
    usedForLEDPage,
    usedForVideowallPage,
    usedForProjectTemplatesPage,
    usedForEditProjectTemplatePage,
    usedForProjectAudioPage,
    // Methods
    syncDropzoneFromDom,
    clearInput,
    clearFilesInDropzone,
    submitFilesInDropzone,
    // Helpers
    storeUploadEvent,
    // Utilities
    isFileTypeValid,
    getTypeFromElementPlus,
    getUploadFileDefaultEditData,
    translateNormalErrorDescription,
    // Duplicate file resolution
    resolveDuplicateFiles
  };
}

export type UseFileUploaderReturn = ReturnType<typeof useFileUploader>;
